import React, { useState } from 'react';
import { FaChartBar, FaTable } from 'react-icons/fa';
import { formatNumber } from './chartTheme';

/**
 * Card frame shared by every graph: title, chart/table toggle, empty state and
 * the "hold the previous render, dimmed" behaviour while a new range loads.
 * The table view is what keeps every plotted value reachable without hovering.
 * `onToggleDashboard` adds the "Show on dashboard" checkbox: ticked graphs are
 * rendered on the admin home page with data till date. `onToggleWebsite` adds
 * the "Show on website" checkbox the same way, for the public clinic website.
 */
export default function ChartCard({
  title,
  subtitle,
  labels = [],
  datasets = [],
  loading = false,
  valueFormatter = formatNumber,
  height = 280,
  onDashboard = false,
  onToggleDashboard,
  dashboardDisabled = false,
  onWebsite = false,
  onToggleWebsite,
  websiteDisabled = false,
  websiteTitle,
  toolbar,
  emptyMessage = 'No data in this range',
  emptyHint = 'Widen the date range to see this graph.',
  children,
}) {
  const [view, setView] = useState('chart');
  const hasData = labels.length > 0 && datasets.some((set) => set.data?.some((value) => value));

  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 text-left">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          {toolbar}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {hasData && (
            <div className="flex shrink-0 rounded-lg bg-slate-100 p-0.5">
              {[
                { key: 'chart', icon: <FaChartBar className="h-3 w-3" />, label: 'Chart view' },
                { key: 'table', icon: <FaTable className="h-3 w-3" />, label: 'Table view' },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  title={option.label}
                  aria-label={option.label}
                  aria-pressed={view === option.key}
                  onClick={() => setView(option.key)}
                  className={`rounded-md px-2 py-1 transition-colors ${
                    view === option.key ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {option.icon}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className={`relative transition-opacity duration-200 ${loading ? 'opacity-40' : 'opacity-100'}`}
        style={{ height }}
      >
        {!hasData ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-4 text-center">
            <p className="text-sm font-medium text-slate-500">{emptyMessage}</p>
            <p className="text-xs text-slate-400">{emptyHint}</p>
          </div>
        ) : view === 'chart' ? (
          children
        ) : (
          <div className="h-full overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="py-1.5 pr-3 font-semibold">Category</th>
                  {datasets.map((set, index) => (
                    <th key={set.label || index} className="py-1.5 pl-3 text-right font-semibold">
                      {set.label || 'Value'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {labels.map((label, rowIndex) => (
                  <tr key={`${label}-${rowIndex}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 pr-3 text-slate-700">{label}</td>
                    {datasets.map((set, index) => (
                      <td key={set.label || index} className="py-1.5 pl-3 text-right font-medium text-slate-800">
                        {valueFormatter(set.data?.[rowIndex] ?? 0)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(onToggleDashboard || onToggleWebsite) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-2.5">
          {onToggleDashboard && (
            <label
              className={`flex items-center gap-2 text-left text-xs font-medium ${
                dashboardDisabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-600 hover:text-slate-800'
              }`}
              title="Ticked graphs appear on the dashboard home page with data till date"
            >
              <input
                type="checkbox"
                checked={onDashboard}
                disabled={dashboardDisabled}
                onChange={(event) => onToggleDashboard(event.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 text-sky-600 accent-sky-600 focus:ring-sky-300 disabled:cursor-not-allowed"
              />
              Show on dashboard
            </label>
          )}
          {onToggleWebsite && (
            <label
              className={`flex items-center gap-2 text-left text-xs font-medium ${
                websiteDisabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-600 hover:text-slate-800'
              }`}
              title={websiteTitle || 'Ticked graphs appear on the clinic website with data till date (counts only)'}
            >
              <input
                type="checkbox"
                checked={onWebsite}
                disabled={websiteDisabled}
                onChange={(event) => onToggleWebsite(event.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 text-sky-600 accent-sky-600 focus:ring-sky-300 disabled:cursor-not-allowed"
              />
              Show on website
            </label>
          )}
        </div>
      )}
    </div>
  );
}
